import {
  AncillaryStatusTypeEnum,
  AncillaryTypeEnum,
  AvailableUpsellOptionsNode,
  PortalAncillaryNode,
  PortalLeadNode,
  PortalOrderNode,
  PortalTierCatalogNode,
  PortalTierCoverageBandNode,
  PortalTierEnum,
} from '@app/services/api/api.types';

export type TierRetentionVariant = 'payBoth' | 'zeroFareDiff' | 'coveredRetention';

const tierSet = new Set(Object.values(PortalTierEnum));

export const isTierAncillaryType = (type: string): type is PortalTierEnum =>
  tierSet.has(type as PortalTierEnum);

export const getPurchasedTierAncillary = (
  portalOrder: PortalOrderNode | null,
): PortalAncillaryNode | undefined =>
  portalOrder?.ancillaries.find(
    (ancillary) =>
      ancillary.status === AncillaryStatusTypeEnum.PURCHASED &&
      isTierAncillaryType(ancillary.type as string),
  );

export const getActiveTierAncillary = (
  portalOrder: PortalOrderNode | null,
): PortalAncillaryNode | undefined => {
  const tierAncillaries =
    portalOrder?.ancillaries.filter((ancillary) => isTierAncillaryType(ancillary.type as string)) ??
    [];

  return (
    tierAncillaries.find((ancillary) => ancillary.status === AncillaryStatusTypeEnum.PURCHASED) ??
    tierAncillaries.find((ancillary) => ancillary.status === AncillaryStatusTypeEnum.AVAILABLE)
  );
};

export const hasPurchasedTierAncillary = (portalOrder: PortalOrderNode | null) =>
  !!getPurchasedTierAncillary(portalOrder);

export const hasActiveTierAncillary = (portalOrder: PortalOrderNode | null) =>
  !!getActiveTierAncillary(portalOrder);

export const getPurchasedTierType = (
  portalOrder: PortalOrderNode | null,
): PortalTierEnum | undefined => {
  const ancillary = getPurchasedTierAncillary(portalOrder);
  return ancillary && isTierAncillaryType(ancillary.type as string)
    ? (ancillary.type as unknown as PortalTierEnum)
    : undefined;
};

export const getActiveTierType = (
  portalOrder: PortalOrderNode | null,
): PortalTierEnum | undefined => {
  const ancillary = getActiveTierAncillary(portalOrder);
  return ancillary && isTierAncillaryType(ancillary.type as string)
    ? (ancillary.type as unknown as PortalTierEnum)
    : undefined;
};

export const getTierRetentionVariant = (
  portalOrder: PortalOrderNode,
  purchasedAncillary: PortalAncillaryNode,
): TierRetentionVariant => {
  const fareDiffPerPax = portalOrder.soldAmountPerAdult ?? 0;
  const retentionGapPerPax = purchasedAncillary.amountPerTraveler ?? 0;

  if (!fareDiffPerPax) {
    return 'zeroFareDiff';
  }

  if (!retentionGapPerPax) {
    return 'coveredRetention';
  }

  return 'payBoth';
};

export const getTierRetentionBannerBody = (
  variant: TierRetentionVariant,
  planName: string,
  fareDiffFormatted: string,
  retentionGapFormatted: string,
) => {
  switch (variant) {
    case 'zeroFareDiff':
      return "Good news — your protection remains active and there's nothing more to pay";
    case 'coveredRetention':
      return 'Good news — your protection remains active on your updated flight. Pay the flight price difference to continue.';
    case 'payBoth':
      return `To continue, please pay a ${fareDiffFormatted} flight price difference and ${retentionGapFormatted} to retain your ${planName}, per passenger.`;
  }
};

export const getRebookingCoverage = (
  pricePerPassenger: number,
  bands: PortalTierCoverageBandNode[],
) => {
  if (!bands.length) {
    return 0;
  }

  for (const band of bands) {
    if (band.priceThreshold == null) {
      return band.coverageAmount;
    }

    if (pricePerPassenger <= band.priceThreshold) {
      return band.coverageAmount;
    }
  }

  return bands[bands.length - 1].coverageAmount;
};

const getCatalogTier = (catalog: PortalTierCatalogNode[], tier: PortalTierEnum) =>
  catalog.find((entry) => entry.tierType === tier);

const getCoverageFromCatalog = (
  catalog: PortalTierCatalogNode[],
  pricePerPassenger: number,
  tier: PortalTierEnum,
) => {
  const entry = getCatalogTier(catalog, tier);
  return entry ? getRebookingCoverage(pricePerPassenger, entry.coverageBands) : 0;
};

const getCashbackFromCatalog = (catalog: PortalTierCatalogNode[], tier: PortalTierEnum) =>
  getCatalogTier(catalog, tier)?.cashbackPerPassenger ?? 0;

export const getTierForRetention = (
  catalog: PortalTierCatalogNode[],
  portalOrder: PortalOrderNode,
  portalCurrencyPipeTransform: (value: number, isFractionClass?: boolean) => string,
  agencyName: string,
) => {
  const activeType = getActiveTierType(portalOrder);

  if (!activeType) {
    return null;
  }

  return (
    getTiers(catalog, portalOrder, portalCurrencyPipeTransform, agencyName).find(
      (tier) => tier.value === activeType,
    ) ?? null
  );
};

export const getTierRetentionPrice = (
  portalOrder: PortalOrderNode | null,
  tier?: PortalTierEnum,
) => {
  if (!portalOrder || !tier) {
    return 0;
  }

  const ancillary = portalOrder.ancillaries.find((a) => a.type === (tier as unknown));

  return ancillary ? Number(ancillary.price) : 0;
};

export const isYourPlan = (tier: { value: unknown }, options: AvailableUpsellOptionsNode) => {
  if (tier.value == null) {
    return options.essential;
  }

  if (tier.value === PortalTierEnum.ESSENTIAL) {
    return !options.essential;
  }

  return false;
};

export const isTierDisabledByUpsellOptions = (
  tier: { value: unknown; price: number },
  options: AvailableUpsellOptionsNode,
) => {
  if (tier.value == null) {
    return false;
  }

  if (tier.price === 0) {
    return true;
  }

  switch (tier.value) {
    case PortalTierEnum.ESSENTIAL:
      return !options.essential;
    case PortalTierEnum.VIP:
      return !options.vip;
    case PortalTierEnum.EXECUTIVE:
      return !options.executive;
    default:
      return false;
  }
};

export const getTiers = (
  catalog: PortalTierCatalogNode[],
  portalOrder: PortalOrderNode,
  portalCurrencyPipeTransform: (value: number, isFractionClass?: boolean) => string,
  agencyName: string,
  availableUpsellOptions?: AvailableUpsellOptionsNode | null,
) => {
  const passengersCount = availableUpsellOptions
    ? portalOrder.numberOfAdults
    : portalOrder.totalPassengers;

  const vipCreditValue = getCashbackFromCatalog(catalog, PortalTierEnum.VIP);
  const executiveCreditValue = getCashbackFromCatalog(catalog, PortalTierEnum.EXECUTIVE);

  const formatMaxRebookingPerPassenger = (tier: PortalTierEnum) =>
    portalCurrencyPipeTransform(
      portalOrder.soldAmountPerAdult +
        getCoverageFromCatalog(catalog, portalOrder.soldAmountPerAdult, tier),
    );

  const formatRebookingCoverageWithBreakdown = (tier: PortalTierEnum) =>
    `${formatMaxRebookingPerPassenger(tier)}/passenger (Flight price ${portalCurrencyPipeTransform(portalOrder.soldAmountPerAdult)} + Rebooking Coverage ${portalCurrencyPipeTransform(getCoverageFromCatalog(catalog, portalOrder.soldAmountPerAdult, tier))})`;

  const rebookingProtectionCopy = (tier: PortalTierEnum) =>
    `In case of airline delays or disruptions, you can rebook the new itinerary for up to ${formatMaxRebookingPerPassenger(tier)}/passenger`;

  return [
    {
      value: undefined,
      disabled: false,
      isYourPlan: false,
      includedInEssential: false,
      price: 0,
      soldAmountPerAdult: 0,
      label: 'Basic',
      badge: '',
      img: 'ticket',
      base: {
        protection: 'No travel protection',
        cancellation: 'No cancellations assistance',
        changes: 'Pay up to $250/passenger for changes',
        concierge: 'Pay up to $75/passenger for post-booking services',
        cashback: 'No bonus travel credits',
      },
      bookingAndExpertSupport: {
        premiumTravelTickets: true,
        dedicatedTravelExpert: true,
        travelConciergeServicesPostBooking: 'Up to $75/passenger',
      },
      airlineDisruptions: {
        travelDisruptionAssistance: false,
        claimWindow: false,
        rebookingCoverage: false,
      },
      voluntaryFlightChanges: {
        changeFee: 'Up to $250/passenger + Fare difference',
      },
      voluntaryFlightCancellations: {
        creditRefunds: false,
        creditValidity: false,
        cancellationWindow: false,
      },
      instantCashback: {
        creditValue: false,
        creditValidity: false,
      },
    },
    {
      value: PortalTierEnum.ESSENTIAL,
      disabled: false,
      isYourPlan: false,
      price: 0,
      soldAmountPerAdult: 0,
      label: 'Essential',
      badge: 'Value deal',
      img: 'luggage',
      base: {
        protection: rebookingProtectionCopy(PortalTierEnum.ESSENTIAL),
        cancellation:
          'Cancel up to 72h before departure for airline credits, if available. Rebooking assistance with the same airline, destination and passengers.',
        changes: 'No change fees',
        concierge: 'Free concierge services',
        cashback: 'No bonus travel credits',
      },
      bookingAndExpertSupport: {
        premiumTravelTickets: true,
        dedicatedTravelExpert: true,
        travelConciergeServicesPostBooking: true,
      },
      airlineDisruptions: {
        travelDisruptionAssistance: true,
        claimWindow: "Until the first flight's departure",
        rebookingCoverage: `${formatMaxRebookingPerPassenger(PortalTierEnum.ESSENTIAL)}/passenger`,
      },
      voluntaryFlightChanges: {
        changeFee: 'Fare difference only',
      },
      voluntaryFlightCancellations: {
        creditRefunds: 'Cancel for Airline credits if applicable',
        creditRefundsTooltip:
          'We work with the airlines to cancel and rebook. However, the passengers or destination cannot be changed and the next flight must be within 1 year from the purchase date of the original ticket. Your travel expert can explain more. Not all airlines offer these options.',
        creditValidity: '12 months if applicable',
        cancellationWindow: '72 hours before departure',
      },
      instantCashback: {
        creditValue: false,
        creditValidity: false,
      },
    },
    {
      value: PortalTierEnum.VIP,
      disabled: false,
      isYourPlan: false,
      price: 0,
      soldAmountPerAdult: 0,
      label: 'VIP',
      badge: "Traveler's Favorite",
      img: 'crown',
      base: {
        protection: rebookingProtectionCopy(PortalTierEnum.VIP),
        cancellation:
          'Cancel up to 48h before departure for 100% credits or 75% cash. Rebooking assistance with any new itinerary.',
        changes: 'No change fees',
        concierge: 'Free concierge services',
        cashback: `${portalCurrencyPipeTransform(vipCreditValue * passengersCount)} bonus travel credits for your next trip`,
      },
      bookingAndExpertSupport: {
        premiumTravelTickets: true,
        dedicatedTravelExpert: true,
        travelConciergeServicesPostBooking: true,
      },
      airlineDisruptions: {
        travelDisruptionAssistance: true,
        claimWindow: "Until the last flight's departure",
        rebookingCoverage: formatRebookingCoverageWithBreakdown(PortalTierEnum.VIP),
      },
      voluntaryFlightChanges: {
        changeFee: 'Fare difference only',
      },
      voluntaryFlightCancellations: {
        creditRefunds: `100% ${agencyName} credits or 75% cash`,
        creditRefundsTooltip:
          'Choose between a 100% credit refund or 75% cash back into your original payment method.',
        creditValidity: '15 months',
        cancellationWindow: '48 hours before departure',
      },
      instantCashback: {
        creditValue: `${portalCurrencyPipeTransform(vipCreditValue)}/passenger (${portalCurrencyPipeTransform(vipCreditValue * passengersCount)} total)`,
        creditValidity: '12 months',
      },
    },
    {
      value: PortalTierEnum.EXECUTIVE,
      disabled: false,
      isYourPlan: false,
      price: 0,
      soldAmountPerAdult: 0,
      label: 'Executive',
      badge: 'Ultimate Peace',
      img: 'bell',
      base: {
        protection: rebookingProtectionCopy(PortalTierEnum.EXECUTIVE),
        cancellation:
          'Cancel up to 24h before departure for 100% credits or 85% cash with extended validity. Rebooking assistance with any new itinerary.',
        changes: 'No change fees',
        concierge: 'Free concierge services',
        cashback: `${portalCurrencyPipeTransform(executiveCreditValue * passengersCount)} bonus travel credits for your next trip`,
      },
      bookingAndExpertSupport: {
        premiumTravelTickets: true,
        dedicatedTravelExpert: true,
        travelConciergeServicesPostBooking: true,
      },
      airlineDisruptions: {
        travelDisruptionAssistance: true,
        claimWindow: "Until the last flight's departure",
        rebookingCoverage: formatRebookingCoverageWithBreakdown(PortalTierEnum.EXECUTIVE),
      },
      voluntaryFlightChanges: {
        changeFee: 'Fare difference only',
      },
      voluntaryFlightCancellations: {
        creditRefunds: `100% ${agencyName} credits or 85% cash`,
        creditRefundsTooltip:
          'Choose between a 100% credit refund or 85% cash back into your original payment method.',
        creditValidity: '18 months',
        cancellationWindow: '24 hours before departure',
      },
      instantCashback: {
        creditValue: `${portalCurrencyPipeTransform(executiveCreditValue)}/passenger (${portalCurrencyPipeTransform(executiveCreditValue * passengersCount)} total)`,
        creditValidity: '24 months',
      },
    },
  ]
    .map((tier) => {
      const foundAncillary = portalOrder.ancillaries.find(
        (ancillary) => ancillary.type === (tier.value as unknown),
      );

      if (foundAncillary) {
        tier.price = Number(foundAncillary.price);
        tier.soldAmountPerAdult = Number(foundAncillary.amountPerTraveler);
      } else {
        tier.price = 0;
        tier.soldAmountPerAdult = 0;
      }

      tier.disabled = availableUpsellOptions
        ? isTierDisabledByUpsellOptions(tier, availableUpsellOptions)
        : isTierDisabled(portalOrder, tier);

      tier.isYourPlan = availableUpsellOptions ? isYourPlan(tier, availableUpsellOptions) : false;

      return tier;
    })
    .map((tier, _, tiers) => {
      const essentialIsYourPlan = tiers.find(
        (t) => t.value === PortalTierEnum.ESSENTIAL,
      )?.isYourPlan;

      if (tier.value == null) {
        tier.includedInEssential = !!essentialIsYourPlan;
      }

      return tier;
    });
};

export type TierPlan = ReturnType<typeof getTiers>[number];

const isTierDisabled = (portalOrder: PortalOrderNode, tier: { value: unknown }) => {
  if (portalOrder.ancillaries?.length === 3) {
    return false;
  }

  const ancillariesTypes = portalOrder.ancillaries.map((ancillary) => ancillary.type);

  return !ancillariesTypes.includes(tier.value as AncillaryTypeEnum);
};

export const getRows = (portalLead: PortalLeadNode | null) => [
  {
    label: 'Booking & Expert support',
    key: 'bookingAndExpertSupport',
    items: [
      {
        label: 'Premium travel tickets',
        key: 'premiumTravelTickets',
      },
      {
        label: `Dedicated travel expert ${portalLead?.salesAgent?.fullName}`,
        key: 'dedicatedTravelExpert',
      },
      {
        label: 'Post-Booking services',
        key: 'travelConciergeServicesPostBooking',
        tooltip:
          'We will help you with online check-in, reserving paid seat options, booking special assistance like mobility devices or medical equipment, booking extra luggage or adding sports equipment, carrying pets and handling airline complaints post travel.',
      },
    ],
  },
  {
    label: 'Disruptions caused by Airlines',
    key: 'airlineDisruptions',
    items: [
      {
        label: 'Travel Disruption Assistance',
        key: 'travelDisruptionAssistance',
        tooltip:
          'If your flights are delayed or cancelled, our team will rebook you when the options from your airlines are not reasonable. We even top up your fare with additional rebooking coverage from our side to cover more expensive fares.',
      },
      {
        label: 'Claim window',
        key: 'claimWindow',
        tooltip:
          'On the Essential plan, you get assistance up until your first outbound flight departs, whereas on VIP and Executive, you can claim assistance up until the departure of the last inbound flight of the itinerary.',
      },
      {
        label: 'Rebooking coverage',
        key: 'rebookingCoverage',
        tooltip:
          'In case of airline delays or disruptions, we will top up your fare value to cover the new itinerary.',
      },
    ],
  },
  {
    label: 'Voluntary flight changes',
    key: 'voluntaryFlightChanges',
    items: [
      {
        label: 'Change fee',
        key: 'changeFee',
        tooltip:
          'This is our service fee apart from the fare difference originating from the airline. Changes made less than 24 hours are free in all plans. This fee can be discounted or waived on some fares. Please consult your travel expert.',
      },
    ],
  },
  {
    label: 'Voluntary flight cancellations',
    key: 'voluntaryFlightCancellations',
    items: [
      {
        label: 'Credit refunds',
        key: 'creditRefunds',
        tooltip:
          'When you cancel, you can receive credits that can be used to book flights again for the same value.',
      },
      {
        label: 'Credit validity',
        key: 'creditValidity',
        tooltip: 'The validity starts from the date of the original ticket purchase',
      },
      {
        label: 'Cancellation window',
        key: 'cancellationWindow',
        tooltip:
          'This is the time window from the exact date and time of departure of your first outbound flight',
      },
    ],
  },
  {
    label: 'Instant Travel credits',
    key: 'instantCashback',
    items: [
      {
        label: 'Credit value',
        key: 'creditValue',
        tooltip:
          'Earn bonus credits from your purchase that can be used to purchase flights or travel protection products in the future.',
      },
      {
        label: 'Credit validity',
        key: 'creditValidity',
        tooltip: 'The validity starts from the date of the original ticket purchase',
      },
    ],
  },
];

export const isTiersFeature = (portalOrder: PortalOrderNode | null) => {
  if (!portalOrder) return false;

  const tierSet = new Set(Object.values(PortalTierEnum));

  return portalOrder.ancillaries.every((ancillary) => {
    return tierSet.has(ancillary.type as unknown as PortalTierEnum);
  });
};
